// FILE: NewThreadDialog.tsx
// Purpose: The dialog behind "New thread" — pick project, environment, name,
//          model/agent and whether the project's setup action runs.
// Layer: Web components
//
// Confirming materializes the thread and its worktree immediately (see
// `useCreateThreadFromDialog`), rather than the historical behaviour of
// deferring both until the first message. Every field is pre-filled, so Enter
// alone is a valid way to use this dialog.

import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import type {
  EnvironmentId,
  ManagedAgentId,
  ModelSelection,
  ModelSlug,
  ProjectId,
  ProviderKind,
  QaOperationKind,
} from "@taskin-code/contracts";
import {
  filterQaCapableAgents,
  NO_QA_CAPABLE_AGENT_MESSAGE,
} from "@taskin-code/shared/managedAgentQaCapability";

import { EnvironmentsClient } from "../auth/EnvironmentsClient";

import type { DraftThreadEnvMode } from "../composerDraftStore";
import { resolvePreferredComposerModelSelection } from "../composerDraftStore";
import { useDefaultModel } from "../auth/authStore";
import { useModelSelectionPolicy } from "../hooks/useModelSelectionPolicy";
import { useAppSettings } from "../appSettings";
import { useProviderModelCatalog } from "../hooks/useProviderModelCatalog";
import type { PlaneIssueSummary } from "../lib/planeClient";
import { ProviderModelPicker } from "./chat/ProviderModelPicker";
import { NewThreadDialogPlaneIssueField } from "./NewThreadDialogPlaneIssueField";
import { NewThreadDialogPullRequestField } from "./NewThreadDialogPullRequestField";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { useProjectUiCapabilities } from "~/auth/projectUiCapabilities";
import {
  environmentOptionLabel,
  isEnvModeLockedByPullRequest,
  qaAgentModelSelection,
  qaOperationCopy,
  qaOperationThreadName,
  resolveQaAgentId,
  isWorktreeFieldApplicable,
  projectHasPlaneBoard,
  resolveEnvModeForProject,
  resolveDefaultEnvironmentId,
  resolveIssueThreadName,
  resolveNewThreadDialogDefaults,
  resolvePullRequestThreadName,
  resolveWorktreeBranchPreview,
  setupScriptForProject,
  shouldReplaceThreadNameWithIssue,
  shouldReplaceThreadNameWithPullRequest,
  type NewThreadDialogAgent,
  type NewThreadDialogEnvironment,
  type NewThreadDialogProject,
  type NewThreadDialogPullRequest,
} from "./NewThreadDialog.logic";

/** Sentinel for the environment select: Base UI treats an empty value as "no selection". */
const NO_ENVIRONMENT = "__none__";
const NO_ENVIRONMENT_LABEL = "Ninguno (worktree temporal)";

/** Stable empty set, so a project with no linked pull requests does not remount the picker. */
const EMPTY_NUMBERS: ReadonlySet<number> = new Set<number>();

const ENV_MODE_OPTIONS: ReadonlyArray<{ id: DraftThreadEnvMode; label: string; hint: string }> = [
  { id: "worktree", label: "Worktree", hint: "Isolated branch and checkout" },
  { id: "local", label: "Local", hint: "Works directly in the project" },
];

/** Is a select/menu popup actually on screen right now? */
function hasVisiblePopup(): boolean {
  return Array.from(document.querySelectorAll('[role="listbox"], [role="menu"]')).some(
    (element) =>
      element instanceof HTMLElement &&
      (element.checkVisibility?.() ?? element.offsetParent !== null),
  );
}

export interface NewThreadDialogSubmit {
  projectId: ProjectId;
  envMode: DraftThreadEnvMode;
  /** Raw user input; empty means "generate a throwaway branch name". */
  name: string;
  modelSelection: ModelSelection;
  runSetupScript: boolean;
  /** The Plane issue this thread is being opened for, if one was picked. */
  planeIssue: PlaneIssueSummary | null;
  /**
   * The pull request this thread is being opened on, if one was picked. Its head branch is what
   * the worktree is cut from, instead of a new branch off the project's base.
   */
  pullRequest: NewThreadDialogPullRequest | null;
  /**
   * The persistent environment the thread runs in, or `null` for an ordinary
   * worktree. Always stated, never omitted: absent means "server, you decide",
   * and this dialog has just asked the person.
   */
  environmentId: EnvironmentId | null;
  /**
   * Set when this thread is being opened to operate an environment. The server
   * re-checks the agent against it before the thread exists, so it is a claim
   * the command has to carry, not a hint.
   */
  qaOperation: QaOperationKind | null;
  /** Text to leave written in the composer, unsent. */
  prompt: string | null;
}

export interface NewThreadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: ReadonlyArray<NewThreadDialogProject>;
  /** Project to pre-select — normally the one in focus when the dialog opened. */
  preferredProjectId: ProjectId | null;
  defaultEnvMode: DraftThreadEnvMode;
  /** Pre-selected provider, for the per-provider shortcuts. */
  initialProvider?: ProviderKind | null;
  /** Pull request to pre-select — set by the entry points that already know one. */
  initialPullRequest?: NewThreadDialogPullRequest | null;
  /** Environment to pre-select, set by «Abrir hilo aquí» on an environment card. */
  initialEnvironmentId?: EnvironmentId | null;
  /**
   * The environment operation this thread is being opened for, set by the
   * card's «Actualizar», «Resetear datos» and «Copiar desde…» buttons. Non-null
   * fixes the project and the environment, and narrows the agent list to the
   * agents that can actually carry the operation out.
   */
  initialQaOperation?: QaOperationKind | null;
  /** Message to leave written in the composer — the operation buttons' text. */
  initialPrompt?: string | null;
  /** Pull request numbers that already have a thread, keyed by project, so the picker can say so. */
  linkedPullRequestNumbersByProject: ReadonlyMap<ProjectId, ReadonlySet<number>>;
  /** Rejecting keeps the dialog open and shows the reason. */
  onCreate: (input: NewThreadDialogSubmit) => Promise<void>;
}

export function NewThreadDialog({
  open,
  onOpenChange,
  projects,
  preferredProjectId,
  defaultEnvMode,
  initialProvider,
  initialPullRequest,
  initialEnvironmentId,
  initialQaOperation,
  initialPrompt,
  linkedPullRequestNumbersByProject,
  onCreate,
}: NewThreadDialogProps) {
  const navigate = useNavigate();
  const { settings } = useAppSettings();
  const userDefaultModel = useDefaultModel();
  const modelSelectionPolicy = useModelSelectionPolicy();
  const { modelOptionsByProvider, visibleManagedAgents, providerStatuses } =
    useProviderModelCatalog();

  const [projectId, setProjectId] = useState<ProjectId | null>(null);
  const { canSelectModel, canViewPullRequests } = useProjectUiCapabilities(projectId);
  const [envMode, setEnvMode] = useState<DraftThreadEnvMode>("worktree");
  const [name, setName] = useState("");
  const [runSetupScript, setRunSetupScript] = useState(true);
  const [modelSelection, setModelSelection] = useState<ModelSelection | null>(null);
  const [planeIssue, setPlaneIssue] = useState<PlaneIssueSummary | null>(null);
  // What we last set `name` to from a `planeIssue` pick, so a later pick can
  // tell "still untouched" from "the person edited it" — see
  // `shouldReplaceThreadNameWithIssue`.
  const [planeIssueNameAutofill, setPlaneIssueNameAutofill] = useState<string | null>(null);
  const [pullRequest, setPullRequest] = useState<NewThreadDialogPullRequest | null>(null);
  // Environments the person may work in on the selected project, and which one
  // this thread will use. `touched` is what keeps a default from overwriting a
  // choice once the list finishes loading.
  const [environments, setEnvironments] = useState<ReadonlyArray<NewThreadDialogEnvironment>>([]);
  const [environmentId, setEnvironmentId] = useState<EnvironmentId | null>(null);
  const [environmentTouched, setEnvironmentTouched] = useState(false);
  // Tracked apart from `planeIssueNameAutofill` on purpose: picking an issue after a pull request
  // must not mistake the pull request's autofill for a name the person typed.
  const [pullRequestNameAutofill, setPullRequestNameAutofill] = useState<string | null>(null);
  // The environment operation this dialog was opened for, and the agent that
  // will carry it out. Both are null in the ordinary "New thread" case.
  const [qaOperation, setQaOperation] = useState<QaOperationKind | null>(null);
  const [qaAgentId, setQaAgentId] = useState<ManagedAgentId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === projectId) ?? null,
    [projects, projectId],
  );

  // Re-seed every time the dialog opens: a stale selection from a previous
  // open is more surprising than a fresh default.
  useEffect(() => {
    if (!open) {
      setIsSubmitting(false);
      return;
    }
    const defaults = resolveNewThreadDialogDefaults({
      projects,
      preferredProjectId,
      defaultEnvMode,
    });
    setProjectId(defaults.projectId);
    setName(defaults.name);
    setRunSetupScript(defaults.runSetupScript);
    setModelSelection(null);
    setPlaneIssue(null);
    setPlaneIssueNameAutofill(null);
    setEnvironments([]);
    setEnvironmentId(initialEnvironmentId ?? null);
    setEnvironmentTouched(initialEnvironmentId != null);
    setQaOperation(initialQaOperation ?? null);
    setQaAgentId(null);
    setError(null);
    const seededPullRequest = initialPullRequest ?? null;
    setPullRequest(seededPullRequest);
    if (seededPullRequest) {
      // A pull request is always a worktree, whatever the person's default is — see
      // `isEnvModeLockedByPullRequest`.
      setEnvMode("worktree");
      const autofill = resolvePullRequestThreadName(
        seededPullRequest.number,
        seededPullRequest.title,
      );
      setName(autofill);
      setPullRequestNameAutofill(autofill);
    } else {
      setEnvMode(defaults.envMode);
      setPullRequestNameAutofill(null);
    }
    // `projects` changes identity on every store sync; seeding is keyed on the
    // dialog opening, not on the project list churning underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Which environments this project offers the person. Fetched per open and per
  // project switch, and never fatal: `p.env.use` is a project grant, so "no
  // environments" and "not allowed any" are the same empty list here, and the
  // dialog still creates ordinary worktree threads either way.
  useEffect(() => {
    if (!open || projectId === null) return;
    let cancelled = false;
    void EnvironmentsClient.listUsable(projectId)
      .then((rows) => {
        if (cancelled) return;
        const usable = rows.map(
          (row): NewThreadDialogEnvironment => ({
            id: row.id,
            kind: row.kind,
            name: row.name,
            status: row.status,
          }),
        );
        setEnvironments(usable);
        // Only fills a blank: once the person has answered, a list arriving
        // late must not overrule them.
        setEnvironmentId((current) =>
          environmentTouched
            ? current
            : resolveDefaultEnvironmentId({
                environments: usable,
                preferred: initialEnvironmentId ?? null,
              }),
        );
      })
      .catch(() => {
        if (!cancelled) setEnvironments([]);
      });
    return () => {
      cancelled = true;
    };
    // `environmentTouched` is read, not depended on: re-running because the
    // person picked something would re-fetch the list on every choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, initialEnvironmentId]);

  /**
   * The agents that may be handed this operation.
   *
   * `filterQaCapableAgents` and not a local predicate: the server refuses the
   * same command with the same rule, and two readings of "capable" that drift
   * apart would show a PM a menu of agents whose thread creation is then
   * rejected.
   *
   * Narrowed by the operation, not only by the base rule: «Probar sobre QA»
   * also needs `qa_try_pr` and `qa_back_to_base`, and an agent offered without
   * them would be refused by the route — or, worse, accepted and left unable to
   * do the one thing it was opened for.
   */
  const qaCapableAgents = useMemo(
    (): ReadonlyArray<NewThreadDialogAgent> =>
      qaOperation === null ? [] : filterQaCapableAgents(visibleManagedAgents, qaOperation),
    [qaOperation, visibleManagedAgents],
  );

  // Only fills a blank, like the environment above: the agent list arrives
  // asynchronously and must not overrule a choice already made.
  useEffect(() => {
    if (qaOperation === null) return;
    setQaAgentId((current) => resolveQaAgentId({ agents: qaCapableAgents, current }));
  }, [qaOperation, qaCapableAgents]);

  const selectedQaAgent =
    qaAgentId === null ? null : (qaCapableAgents.find((agent) => agent.id === qaAgentId) ?? null);

  // The model the thread would start with if the user never touches the picker.
  const resolvedModelSelection = useMemo((): ModelSelection => {
    if (modelSelection) {
      return modelSelection;
    }
    const projectDefault =
      projects.find((project) => project.id === projectId)?.defaultModelSelection ?? null;
    return resolvePreferredComposerModelSelection({
      draft: null,
      threadModelSelection: null,
      projectModelSelection: projectDefault,
      userDefaultModelSelection: userDefaultModel,
      defaultProvider: initialProvider ?? settings.defaultProvider,
      policy: modelSelectionPolicy,
    });
  }, [
    modelSelection,
    projects,
    projectId,
    userDefaultModel,
    initialProvider,
    settings.defaultProvider,
    modelSelectionPolicy,
  ]);

  const readyEnvironments = environments.filter((environment) => environment.status === "ready");
  const selectedEnvironment =
    environmentId === null
      ? null
      : (readyEnvironments.find((environment) => environment.id === environmentId) ?? null);
  const setupScript = setupScriptForProject(selectedProject);
  // An operation runs inside an environment that already exists: there is no
  // worktree to name, no setup action to arm and no branch to preview.
  const isQaOperation = qaOperation !== null;
  const qaCopy = qaOperation === null ? null : qaOperationCopy(qaOperation);
  const showWorktreeFields = !isQaOperation && isWorktreeFieldApplicable(envMode);
  const branchPreview = resolveWorktreeBranchPreview(name);
  const bareRepositoryLocksEnvMode = selectedProject?.isBareRepository === true;
  const pullRequestLocksEnvMode = isEnvModeLockedByPullRequest(pullRequest);
  const envModeLocked = bareRepositoryLocksEnvMode || pullRequestLocksEnvMode;
  // In operation mode the agent is not a preference: without one there is
  // nothing to open the thread with, and the server would refuse it anyway.
  const canSubmit =
    projectId !== null &&
    !isSubmitting &&
    (!isQaOperation || (selectedQaAgent !== null && selectedEnvironment !== null));

  const handleProjectChange = (nextProjectId: ProjectId) => {
    setProjectId(nextProjectId);
    setError(null);
    const nextProject = projects.find((project) => project.id === nextProjectId) ?? null;
    setEnvMode((current) => resolveEnvModeForProject(nextProject, current));
    // A setup action belongs to a project; switching projects re-arms the
    // opt-out rather than carrying over the previous answer.
    setRunSetupScript(true);
    // An issue belongs to the board of the project it came from; carrying it
    // across a project switch would silently link the new thread to an issue
    // on a board it has no access to.
    setPlaneIssue(null);
    setPlaneIssueNameAutofill(null);
    // Same reasoning as the issue above: a pull request belongs to the repository it came from.
    setPullRequest(null);
    setPullRequestNameAutofill(null);
    // An environment belongs to one project; carrying it over would ask the
    // server to run a thread in another project's workspace.
    setEnvironments([]);
    setEnvironmentId(null);
    setEnvironmentTouched(false);
  };

  const handlePullRequestChange = (picked: NewThreadDialogPullRequest | null) => {
    setPullRequest(picked);
    if (picked === null) {
      return;
    }
    setEnvMode("worktree");
    // A pull request is checked out into its own worktree, so it cannot also
    // run inside an environment: the two are different answers to "where".
    setEnvironmentId(null);
    setEnvironmentTouched(true);
    const autofill = resolvePullRequestThreadName(picked.number, picked.title);
    if (shouldReplaceThreadNameWithPullRequest(name, pullRequestNameAutofill)) {
      setName(autofill);
    }
    setPullRequestNameAutofill(autofill);
  };

  const handlePlaneIssueChange = (issue: PlaneIssueSummary | null) => {
    setPlaneIssue(issue);
    if (issue === null) {
      return;
    }
    const autofill = resolveIssueThreadName(issue.ref, issue.title);
    if (shouldReplaceThreadNameWithIssue(name, planeIssueNameAutofill)) {
      setName(autofill);
    }
    setPlaneIssueNameAutofill(autofill);
  };

  /**
   * Enter confirms from anywhere in the dialog — the whole point of pre-filling
   * every field. A plain `type="submit"` is not enough: the segmented controls
   * are real buttons, so after clicking one, Enter would just re-press it.
   */
  const handleDialogKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.nativeEvent.isComposing) return;
    if (event.target instanceof HTMLTextAreaElement) return;
    // An open select or model picker owns Enter while it is showing. Presence
    // in the DOM is not the test: Base UI keeps a closed select's listbox
    // mounted and hidden, which would swallow every Enter.
    if (hasVisiblePopup()) return;
    event.preventDefault();
    void handleSubmit();
  };

  const handleSubmit = async () => {
    if (!canSubmit || projectId === null) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onCreate({
        projectId,
        envMode,
        name:
          qaOperation !== null
            ? qaOperationThreadName(
                qaOperation,
                selectedEnvironment?.name ?? selectedProject?.name ?? "el entorno",
              )
            : showWorktreeFields
              ? name
              : "",
        // An operation is run *by* the agent that was picked for it, on the
        // provider that agent was configured for — never on the person's
        // default, where its QA tools would not be mounted.
        modelSelection:
          selectedQaAgent !== null
            ? qaAgentModelSelection(selectedQaAgent)
            : resolvedModelSelection,
        runSetupScript: showWorktreeFields && setupScript !== null && runSetupScript,
        planeIssue,
        pullRequest,
        environmentId: pullRequest === null ? environmentId : null,
        qaOperation,
        prompt: qaOperation === null ? null : (initialPrompt ?? null),
      });
      onOpenChange(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create the thread.");
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        className="max-w-lg"
        onKeyDown={handleDialogKeyDown}
        // Without this the dialog focuses its first focusable child — the
        // project combobox — which swallows Enter to open its own list.
        initialFocus={() => nameInputRef.current ?? confirmButtonRef.current}
      >
        <DialogHeader>
          <DialogTitle>{qaCopy?.title ?? "New thread"}</DialogTitle>
          <DialogDescription>
            {qaCopy?.description ??
              "Everything is pre-filled — press Enter to start with these defaults."}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSubmit();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="new-thread-project">Project</Label>
              <Select
                value={projectId ?? ""}
                onValueChange={(value) => {
                  if (!value) return;
                  handleProjectChange(value as ProjectId);
                }}
              >
                <SelectTrigger
                  id="new-thread-project"
                  className="w-full"
                  // An operation belongs to one environment, and an environment
                  // to one project: changing it here would leave the two
                  // pointing at different repositories.
                  disabled={isSubmitting || isQaOperation || projects.length === 0}
                >
                  <SelectValue>{selectedProject?.name ?? "Select a project"}</SelectValue>
                </SelectTrigger>
                <SelectPopup>
                  {projects.map((project) => (
                    <SelectItem hideIndicator key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            </div>

            {!isQaOperation && selectedProject && projectHasPlaneBoard(selectedProject) && (
              <div className="space-y-1.5">
                <Label>Issue de Plane (opcional)</Label>
                <NewThreadDialogPlaneIssueField
                  projectId={selectedProject.id}
                  value={planeIssue}
                  disabled={isSubmitting}
                  onChange={handlePlaneIssueChange}
                />
              </div>
            )}

            {!isQaOperation && selectedProject && canViewPullRequests && (
              <div className="space-y-1.5">
                <Label>Pull request (opcional)</Label>
                <NewThreadDialogPullRequestField
                  projectId={selectedProject.id}
                  value={pullRequest}
                  disabled={isSubmitting}
                  linkedNumbers={
                    linkedPullRequestNumbersByProject.get(selectedProject.id) ?? EMPTY_NUMBERS
                  }
                  onChange={handlePullRequestChange}
                />
                {pullRequest ? (
                  <p className="text-xs text-muted-foreground">
                    El hilo partirá de <span className="font-mono">{pullRequest.headBranch}</span>,
                    la rama de este pull request, en vez de una rama nueva.
                  </p>
                ) : null}
              </div>
            )}

            {/* The persistent environment, when the project has any this person
                may use. Separate from the Worktree/Local control below and
                above it on purpose: it answers "which workspace", and the
                answer makes the other question moot. */}
            {environments.length > 0 && (
              <div className="space-y-1.5">
                <Label htmlFor="new-thread-environment">Entorno</Label>
                <Select
                  value={environmentId ?? NO_ENVIRONMENT}
                  onValueChange={(value) => {
                    if (!value) return;
                    setEnvironmentTouched(true);
                    setEnvironmentId(value === NO_ENVIRONMENT ? null : (value as EnvironmentId));
                  }}
                >
                  <SelectTrigger
                    id="new-thread-environment"
                    className="w-full"
                    disabled={isSubmitting || isQaOperation || pullRequest !== null}
                  >
                    <SelectValue>
                      {selectedEnvironment === null
                        ? NO_ENVIRONMENT_LABEL
                        : environmentOptionLabel(selectedEnvironment)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectPopup>
                    <SelectItem hideIndicator value={NO_ENVIRONMENT}>
                      {NO_ENVIRONMENT_LABEL}
                    </SelectItem>
                    {readyEnvironments.map((environment) => (
                      <SelectItem hideIndicator key={environment.id} value={environment.id}>
                        {environmentOptionLabel(environment)}
                      </SelectItem>
                    ))}
                  </SelectPopup>
                </Select>
                {pullRequest !== null ? (
                  <p className="text-xs text-muted-foreground">
                    Un pull request se abre en su propio worktree, así que este hilo no entra en
                    ningún entorno.
                  </p>
                ) : selectedEnvironment !== null ? (
                  <p className="text-xs text-muted-foreground">
                    El hilo trabaja dentro del entorno: su rama, sus datos y su preview. No se crea
                    worktree nuevo.
                  </p>
                ) : null}
              </div>
            )}

            {!isQaOperation && selectedEnvironment === null && (
              <div className="space-y-1.5">
                <Label>Environment</Label>
                <div className="flex flex-wrap gap-2">
                  {ENV_MODE_OPTIONS.map((option) => {
                    const isSelected = option.id === envMode;
                    const isDisabled = isSubmitting || (envModeLocked && option.id === "local");
                    return (
                      <button
                        key={option.id}
                        type="button"
                        disabled={isDisabled}
                        title={option.hint}
                        className={`rounded-md border px-3 py-1.5 text-xs disabled:opacity-40 ${
                          isSelected
                            ? "border-primary/70 bg-primary/10"
                            : "border-border/70 hover:bg-accent/60"
                        }`}
                        onClick={() => setEnvMode(option.id)}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                {bareRepositoryLocksEnvMode ? (
                  <p className="text-xs text-muted-foreground">
                    This project is a bare repository, so every thread gets its own worktree.
                  </p>
                ) : pullRequestLocksEnvMode ? (
                  <p className="text-xs text-muted-foreground">
                    Abrir un pull request requiere su propio worktree: en modo local habría que
                    cambiar de rama el checkout del proyecto.
                  </p>
                ) : null}
              </div>
            )}

            {showWorktreeFields && (
              <div className="space-y-1.5">
                <Label htmlFor="new-thread-name">Name (optional)</Label>
                <Input
                  ref={nameInputRef}
                  id="new-thread-name"
                  placeholder="Leave empty to name it automatically"
                  value={name}
                  disabled={isSubmitting}
                  onChange={(event) => {
                    setName(event.target.value);
                    setError(null);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {selectedEnvironment !== null ? (
                    "Sólo el título del hilo: la rama la pone el entorno."
                  ) : branchPreview !== null ? (
                    <>
                      Branch: <code>{branchPreview}</code>
                    </>
                  ) : (
                    "A branch name will be generated from your first message."
                  )}
                </p>
              </div>
            )}

            {/* An operation replaces the provider/model picker outright: the
                thread is being opened *for* an agent, and the only agents worth
                offering are the ones whose tool selection can carry it out.
                Derived from `visibleManagedAgents`, so the account's own agent
                allowlist still applies before capability is even asked. */}
            {isQaOperation ? (
              <div className="space-y-1.5">
                <Label htmlFor="new-thread-qa-agent">Agente</Label>
                {qaCapableAgents.length === 0 ? (
                  <p className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                    {NO_QA_CAPABLE_AGENT_MESSAGE}. Dale <code>qa_env_preflight</code>,{" "}
                    <code>qa_env_sync</code> y <code>qa_env_config_set</code> a un agente en{" "}
                    <button
                      type="button"
                      className="underline underline-offset-2"
                      onClick={() => {
                        onOpenChange(false);
                        void navigate({ to: "/settings", search: { section: "agents" } });
                      }}
                    >
                      Ajustes → Agents
                    </button>
                    .
                  </p>
                ) : (
                  <Select
                    value={qaAgentId ?? ""}
                    onValueChange={(value) => {
                      if (!value) return;
                      setQaAgentId(value as ManagedAgentId);
                    }}
                  >
                    <SelectTrigger
                      id="new-thread-qa-agent"
                      className="w-full"
                      // `p.model.select` is a UI gate: without it the person
                      // keeps the preselection rather than losing the dialog.
                      disabled={isSubmitting || !canSelectModel || qaCapableAgents.length === 1}
                    >
                      <SelectValue>{selectedQaAgent?.name ?? "Elige un agente"}</SelectValue>
                    </SelectTrigger>
                    <SelectPopup>
                      {qaCapableAgents.map((agent) => (
                        <SelectItem hideIndicator key={agent.id} value={agent.id}>
                          {agent.name}
                        </SelectItem>
                      ))}
                    </SelectPopup>
                  </Select>
                )}
                {initialPrompt != null && initialPrompt.length > 0 ? (
                  <>
                    <p className="pt-1 text-xs text-muted-foreground">
                      Este mensaje queda escrito en el hilo, sin enviar:
                    </p>
                    <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded border border-border/60 bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
                      {initialPrompt}
                    </pre>
                  </>
                ) : null}
              </div>
            ) : canSelectModel ? (
              <div className="space-y-1.5">
                <Label>Model or agent</Label>
                <div className="flex">
                  <ProviderModelPicker
                    provider={resolvedModelSelection.provider}
                    model={resolvedModelSelection.model as ModelSlug}
                    lockedProvider={null}
                    providers={providerStatuses}
                    modelOptionsByProvider={modelOptionsByProvider}
                    agents={visibleManagedAgents}
                    selectedAgentId={resolvedModelSelection.agentId ?? null}
                    disabled={isSubmitting}
                    onProviderModelChange={(
                      provider: ProviderKind,
                      model: ModelSlug,
                      agentId?: ManagedAgentId,
                    ) => {
                      setModelSelection({
                        provider,
                        model,
                        ...(agentId ? { agentId } : {}),
                      } as ModelSelection);
                    }}
                  />
                </div>
              </div>
            ) : null}

            {showWorktreeFields && setupScript !== null && (
              <label className="flex items-center justify-between gap-3 rounded-md border border-border/70 px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="block truncate">Run “{setupScript.name}” on start</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {setupScript.command}
                  </span>
                </span>
                <Switch
                  checked={runSetupScript}
                  disabled={isSubmitting}
                  onCheckedChange={(checked) => setRunSetupScript(Boolean(checked))}
                />
              </label>
            )}

            {error !== null && <p className="text-sm text-destructive">{error}</p>}
            {projects.length === 0 && (
              <p className="text-sm text-muted-foreground">Add a project first.</p>
            )}
          </form>
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            ref={confirmButtonRef}
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            {...(isQaOperation && qaCapableAgents.length === 0
              ? { title: NO_QA_CAPABLE_AGENT_MESSAGE }
              : {})}
          >
            {isSubmitting ? "Creating..." : (qaCopy?.confirmLabel ?? "Create thread")}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
